"use client";

import { useCallback, useEffect, useState } from "react";

// Structured details for a custom-order request line. name/email/description are
// always present (default ""); the rest are the composed-spec inputs and the
// uploaded inspiration-photo URL, all optional.
export type CustomOrderDetails = {
  name: string;
  email: string;
  description: string;
  referencePiece?: string;
  pieceType?: string;
  goldColor?: string;
  stone?: string;
  stoneColor?: string;
  size?: string;
  photoUrl?: string;
};

// Default price for a customer-added custom-order line (dollars). The owner
// adjusts it in the owner-only Review page before checkout.
export const DEFAULT_CUSTOM_ORDER_PRICE = 300;

export type BagItem = {
  id: string;
  itemName: string;
  pictureUrl: string;
  tagPrice: number | null;
  metal: string;
  stone: string;
  size?: string;
  requestedSize?: string;
  // Present only for "Custom Order Request" items. These ride the checkout
  // `customLines` channel (never the catalog-validated `items` array), and drive
  // custom_orders creation + custom receipt copy at finalize time.
  isCustomOrder?: boolean;
  customOrder?: CustomOrderDetails;
};

const BAG_STORAGE_KEY = "vault88-cart";

export function addBagItem(item: BagItem) {
  if (typeof window === "undefined") return;
  try {
    const stored = window.localStorage.getItem(BAG_STORAGE_KEY);
    const parsed = stored ? (JSON.parse(stored) as BagItem[]) : [];
    if (parsed.some((existing) => existing.id === item.id)) return;
    const next = [...parsed, item];
    window.localStorage.setItem(BAG_STORAGE_KEY, JSON.stringify(next));
    window.dispatchEvent(
      new StorageEvent("storage", { key: BAG_STORAGE_KEY, newValue: JSON.stringify(next) })
    );
  } catch {
    // silent
  }
}

const CUSTOM_ORDER_ID_PREFIX = "custom:";

export function isCustomOrderBagId(id: string): boolean {
  return id.startsWith(CUSTOM_ORDER_ID_PREFIX);
}

// Build a synthetic "Custom Order Request" bag item with a unique, non-catalog
// id. priceDollars is the line price in dollars (null => "price on request").
// Uniqueness uses a random suffix so addBagItem's id-dedupe never collapses two
// custom orders. The inspiration photo (if any) is stored as a URL and mirrored
// into pictureUrl so the cart's image slot renders it.
export function makeCustomOrderBagItem(input: {
  title?: string;
  priceDollars?: number | null;
  name?: string;
  email?: string;
  description?: string;
  referencePiece?: string;
  pieceType?: string;
  goldColor?: string;
  stone?: string;
  stoneColor?: string;
  size?: string;
  photoUrl?: string;
}): BagItem {
  const rand = Math.random().toString(36).slice(2, 10);
  const price = input.priceDollars != null && input.priceDollars > 0 ? input.priceDollars : null;
  const customOrder: CustomOrderDetails = {
    name: input.name ?? "",
    email: input.email ?? "",
    description: input.description ?? "",
  };
  if (input.referencePiece) customOrder.referencePiece = input.referencePiece;
  if (input.pieceType) customOrder.pieceType = input.pieceType;
  if (input.goldColor) customOrder.goldColor = input.goldColor;
  if (input.stone) customOrder.stone = input.stone;
  if (input.stoneColor) customOrder.stoneColor = input.stoneColor;
  if (input.size) customOrder.size = input.size;
  if (input.photoUrl) customOrder.photoUrl = input.photoUrl;
  return {
    id: `${CUSTOM_ORDER_ID_PREFIX}${rand}`,
    itemName: input.title || "Custom Order Request",
    pictureUrl: input.photoUrl || "",
    tagPrice: price,
    metal: "",
    stone: "",
    isCustomOrder: true,
    customOrder,
  };
}

// Empties the bag. Usable outside a React component (e.g. after an in-person
// sale is confirmed). Dispatches a storage event so useBag consumers and the
// header count update immediately.
export function clearBag() {
  if (typeof window === "undefined") return;
  try {
    const next = "[]";
    window.localStorage.setItem(BAG_STORAGE_KEY, next);
    window.dispatchEvent(new StorageEvent("storage", { key: BAG_STORAGE_KEY, newValue: next }));
  } catch {
    // localStorage unavailable (private mode, etc.) — nothing to clear.
  }
}

export function useBag() {
  const [items, setItems] = useState<BagItem[]>([]);

  const readBag = useCallback(() => {
    if (typeof window === "undefined") return;
    try {
      const stored = window.localStorage.getItem(BAG_STORAGE_KEY);
      if (!stored) {
        setItems([]);
        return;
      }
      const parsed = JSON.parse(stored) as BagItem[];
      setItems(parsed);
    } catch (error) {
      console.error("[Bag] Failed to read items", error);
      setItems([]);
    }
  }, []);

  useEffect(() => {
    readBag();

    const handleStorage = (event: StorageEvent) => {
      if (event.key === BAG_STORAGE_KEY) {
        readBag();
      }
    };

    window.addEventListener("storage", handleStorage);
    const interval = window.setInterval(readBag, 2000);

    return () => {
      window.removeEventListener("storage", handleStorage);
      window.clearInterval(interval);
    };
  }, [readBag]);

  const removeItem = useCallback((id: string) => {
    if (typeof window === "undefined") return;
    try {
      const stored = window.localStorage.getItem(BAG_STORAGE_KEY);
      const parsed = stored ? (JSON.parse(stored) as BagItem[]) : [];
      const next = parsed.filter((item) => item.id !== id);
      window.localStorage.setItem(BAG_STORAGE_KEY, JSON.stringify(next));
      setItems(next);
      window.dispatchEvent(new StorageEvent("storage", { key: BAG_STORAGE_KEY, newValue: JSON.stringify(next) }));
    } catch (error) {
      console.error("[Bag] Failed to remove item", error);
    }
  }, []);

  const clear = useCallback(() => {
    clearBag();
    setItems([]);
  }, []);

  return { items, refresh: readBag, removeItem, clearBag: clear };
}
